/**
 * Vincent B995 (Eric 2026-02-26 SPEC STRICK PROD) — Nuage de tags
 * pondéré par usage, affiché sur la page d'accueil des concepteurs.
 *
 * Charge `GET /api/tags/org-vocabulary` (déjà exposé), calcule un
 * font-size log-échelle (compress les extrêmes : REER 50× ne fait pas
 * 50× la taille d'un tag utilisé 1×) et rend chaque tag comme un
 * <Link> vers `/dashboard/search?tags=<tag>&mode=and`. Section
 * repliable pour laisser le contrôle au concepteur.
 *
 * L'objectif : donner un aperçu visuel du vocabulaire de l'org en
 * 1 coup d'œil, et permettre l'exploration cliquable.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Hash, ArrowRight } from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Palette teal → violet en fonction du bucket de fréquence (5 buckets).
// Renforce la lecture rapide : les tags les plus utilisés ressortent.
const BUCKETS = [
  { min: 0.0, cls: "text-zinc-600 hover:text-zinc-800" },       // < 20e pct
  { min: 0.2, cls: "text-teal-600 hover:text-teal-800" },        // 20-40
  { min: 0.4, cls: "text-cyan-700 hover:text-cyan-900" },        // 40-60
  { min: 0.6, cls: "text-indigo-700 hover:text-indigo-900" },    // 60-80
  { min: 0.8, cls: "text-[#002FA7] hover:text-[#001f70] font-semibold" }, // 80-100
];

function _bucketFor(t01) {
  for (let i = BUCKETS.length - 1; i >= 0; i--) {
    if (t01 >= BUCKETS[i].min) return BUCKETS[i].cls;
  }
  return BUCKETS[0].cls;
}

export default function TagCloud() {
  const { lang } = useLang();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Vincent B998 (Eric 2026-02-26 SPEC STRICK PROD) — Filtre par thème
  // de formation : quand un thème est sélectionné, on refetch
  // /tags/org-vocabulary?theme=<name> pour ne compter que les tags des
  // vidéos de ce thème. `themes` : liste des thèmes distincts de l'org
  // (chargée 1× au mount).
  const [themes, setThemes] = useState([]);
  const [activeTheme, setActiveTheme] = useState(""); // "" = tous

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/tags/org-themes`, { withCredentials: true });
        if (mounted) setThemes(data?.themes || []);
      } catch { /* silent */ }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const url = activeTheme
          ? `${API}/api/tags/org-vocabulary?theme=${encodeURIComponent(activeTheme)}`
          : `${API}/api/tags/org-vocabulary`;
        const { data } = await axios.get(url, { withCredentials: true });
        if (!mounted) return;
        setTags(data?.tags || []);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [activeTheme]);

  // Vincent B995 — Font-size en log-échelle pour compresser les extrêmes.
  //   - Cap à 60 tags pour ne pas exploser l'UI.
  //   - Tri par count DESC (les plus visibles en premier).
  //   - Échelle [0.75rem, 1.75rem] proportionnelle à log(count).
  const cloudItems = useMemo(() => {
    if (!tags.length) return [];
    const sorted = [...tags].sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 60);
    const maxLog = Math.log(Math.max(...sorted.map((t) => t.count || 1)) + 1);
    const minLog = Math.log(Math.min(...sorted.map((t) => t.count || 1)) + 1);
    const span = Math.max(maxLog - minLog, 0.0001);
    return sorted.map((t) => {
      const lg = Math.log((t.count || 1) + 1);
      const t01 = (lg - minLog) / span; // 0..1
      const remSize = 0.75 + t01 * 1.0; // 0.75rem → 1.75rem
      return {
        tag: t.tag,
        count: t.count || 0,
        t01,
        style: { fontSize: `${remSize.toFixed(2)}rem`, lineHeight: 1.15 },
        cls: _bucketFor(t01),
      };
    });
  }, [tags]);

  const orphanCount = useMemo(() => tags.filter((t) => (t.count || 0) === 1).length, [tags]);

  if (loading) {
    return (
      <details className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="tag-cloud-panel" open>
        <summary className="cursor-pointer text-sm font-semibold text-zinc-800 flex items-center gap-2">
          <Hash size={16} className="text-[#002FA7]" />
          {lang === "fr" ? "Nuage de tags" : "Tag cloud"}
          <span className="text-xs text-zinc-500 font-normal">
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </span>
        </summary>
      </details>
    );
  }
  if (error) return null; // silent fail (org sans tags, endpoint indisponible…)
  // Vincent B998 — Quand un thème est actif mais renvoie 0 tag, on garde
  // la carte visible avec un empty state pour laisser l'utilisateur
  // changer de thème (sinon l'UI disparaîtrait mystérieusement).
  if (!cloudItems.length && !activeTheme) return null;

  return (
    <details
      className="rounded-xl border border-zinc-200 bg-white p-4"
      data-testid="tag-cloud-panel"
      open
    >
      <summary className="cursor-pointer text-sm font-semibold text-zinc-800 flex items-center gap-2 select-none flex-wrap">
        <Hash size={16} className="text-[#002FA7]" />
        {lang === "fr" ? "Nuage de tags" : "Tag cloud"}
        {/* Vincent B998 (Eric 2026-02-26 SPEC STRICK PROD) — Filtre thème
            de formation. « Tous » = pas de filtre. Le refetch se fait via
            l'effet dépendant de activeTheme. */}
        {themes.length > 0 && (
          <select
            value={activeTheme}
            onChange={(e) => setActiveTheme(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="ml-2 text-xs font-normal px-2 py-1 rounded border border-zinc-300 bg-white text-zinc-700"
            data-testid="tag-cloud-theme-filter"
            title={lang === "fr" ? "Filtrer par thème de formation" : "Filter by training theme"}
          >
            {/* Un seul enfant TEXTE par <option> : plusieurs enfants
                déclenchaient un avertissement d'hydratation React (« <span>
                ne peut pas être enfant de <option> ») à chaque rendu. */}
            <option value="">{lang === "fr" ? "Tous les thèmes" : "All themes"}</option>
            {themes.map((t) => (
              <option key={t.theme} value={t.theme}>{`${t.theme} (${t.count})`}</option>
            ))}
          </select>
        )}
        <span className="text-xs text-zinc-500 font-normal ml-auto flex items-center gap-3">
          <span data-testid="tag-cloud-total-count">
            {tags.length} {lang === "fr" ? "tags" : "tags"}
          </span>
          {orphanCount > 0 && (
            <Link
              to="/dashboard/search"
              className="text-amber-700 hover:text-amber-900 underline decoration-dotted"
              data-testid="tag-cloud-orphans-link"
              onClick={(e) => e.stopPropagation()}
              title={lang === "fr" ? "Voir + gérer les tags orphelins" : "View + manage orphan tags"}
            >
              {orphanCount} {lang === "fr" ? "orphelin(s)" : "orphan(s)"}
            </Link>
          )}
          <Link
            to="/dashboard/tags"
            className="text-[#002FA7] hover:text-[#001f70] inline-flex items-center gap-1"
            data-testid="tag-cloud-manage-link"
            onClick={(e) => e.stopPropagation()}
          >
            {lang === "fr" ? "Gérer" : "Manage"}
            <ArrowRight size={12} />
          </Link>
        </span>
      </summary>
      <div
        className="mt-3 flex flex-wrap gap-x-3 gap-y-2 items-baseline"
        data-testid="tag-cloud-items"
      >
        {cloudItems.length === 0 && activeTheme && (
          <p className="text-xs text-zinc-500 italic" data-testid="tag-cloud-empty-theme">
            {lang === "fr"
              ? `Aucun tag utilisé sur les vidéos du thème « ${activeTheme} ».`
              : `No tag used on videos in theme "${activeTheme}".`}
          </p>
        )}
        {cloudItems.map((it) => (
          <Link
            key={it.tag}
            to={`/dashboard/search?tags=${encodeURIComponent(it.tag)}&mode=and`}
            className={`transition-colors ${it.cls}`}
            style={it.style}
            data-testid={`tag-cloud-item-${it.tag}`}
            title={`${it.tag} — ${it.count}× ${lang === "fr" ? "utilisation(s)" : "usage(s)"}`}
          >
            {it.tag}
          </Link>
        ))}
      </div>
    </details>
  );
}
